import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const NAV_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/real-estate', label: 'Real Estate' },
  { to: '/commercial', label: 'Commercial' },
  { to: '/social-media', label: 'Social Media' },
  { to: '/reviews', label: 'Reviews' },
  { to: '/contact', label: 'Contact' },
];

/**
 * Shared site navigation. Renders desktop links + a working mobile hamburger drawer.
 *
 * Props:
 *   - variant: "dark" | "light"  (default "light")
 *       dark  → black translucent bar (used over video hero on /)
 *       light → white translucent bar (used on inner pages)
 *   - position: "fixed" | "absolute" (default "absolute")
 */
export default function Navbar({ variant = 'light', position = 'absolute' }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Close the menu on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the mobile drawer is open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  const isDark = variant === 'dark';
  const navBg = isDark ? 'bg-black/80' : 'bg-white/90';
  const linkColor = isDark ? 'text-white hover:text-gray-300' : 'text-gray-700 hover:text-gray-900';
  const activeColor = isDark ? 'text-white border-b-2 border-white' : 'text-blue-600 border-b-2 border-blue-600';
  const brandColor = isDark ? 'text-white' : 'text-gray-900';
  const iconColor = isDark ? 'text-white' : 'text-gray-700';
  const positionClass = position === 'fixed' ? 'fixed' : 'absolute';

  return (
    <>
      <nav
        className={`${positionClass} w-full z-50 ${navBg} backdrop-blur-md ${isDark ? '' : 'shadow-sm'}`}
        style={{ top: 'var(--announcement-banner-height, 0px)' }}
        data-testid="site-nav"
      >
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Brand */}
            <Link to="/" className="flex items-center space-x-3 cursor-pointer">
              <img
                src="https://images.squarespace-cdn.com/content/v1/6329980709c417765c18ef9c/1663928993488-9PHELX1GHR1MGT4SNOLP/minchio+brothers+logo.png?format=100w"
                alt="Minchio Brothers Logo"
                className="h-10 w-10 md:h-12 md:w-12"
              />
              <span className={`${brandColor} font-bold text-base md:text-xl tracking-wider`}>
                MINCHIO BROTHERS
              </span>
            </Link>

            {/* Desktop links */}
            <div className="hidden md:flex items-center space-x-8">
              {NAV_LINKS.map(({ to, label }) => {
                const active = location.pathname === to;
                return (
                  <Link
                    key={to}
                    to={to}
                    className={`cursor-pointer font-medium transition-colors ${
                      active ? activeColor + ' font-semibold' : linkColor
                    }`}
                  >
                    {label}
                  </Link>
                );
              })}
            </div>

            {/* Right cluster: Instagram + mobile hamburger */}
            <div className="flex items-center space-x-3 md:space-x-4">
              <a
                href="https://www.instagram.com/minchiobrothers/"
                target="_blank"
                rel="noopener noreferrer"
                className={`${iconColor} hover:opacity-80 cursor-pointer`}
                aria-label="Instagram"
              >
                <i className="ri-instagram-line text-2xl"></i>
              </a>
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-label={open ? 'Close menu' : 'Open menu'}
                aria-expanded={open}
                aria-controls="mobile-menu"
                className={`md:hidden p-1 -mr-1 ${iconColor} cursor-pointer`}
                data-testid="mobile-menu-toggle"
              >
                <i className={`${open ? 'ri-close-line' : 'ri-menu-line'} text-3xl`}></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile drawer + backdrop */}
      <div
        className={`md:hidden fixed inset-0 z-40 transition-opacity duration-200 ${
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        aria-hidden={!open}
        onClick={() => setOpen(false)}
      >
        <div className="absolute inset-0 bg-black/60" />
      </div>

      <aside
        id="mobile-menu"
        className={`md:hidden fixed top-0 right-0 z-50 h-full w-72 max-w-[85vw] bg-white shadow-2xl transform transition-transform duration-300 ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
        data-testid="mobile-menu-drawer"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <span className="font-bold text-gray-900 tracking-wider">MENU</span>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close menu"
            className="p-1 -mr-1 text-gray-700 cursor-pointer"
            data-testid="mobile-menu-close"
          >
            <i className="ri-close-line text-3xl"></i>
          </button>
        </div>
        <nav className="flex flex-col py-2">
          {NAV_LINKS.map(({ to, label }) => {
            const active = location.pathname === to;
            return (
              <Link
                key={to}
                to={to}
                onClick={() => setOpen(false)}
                className={`px-5 py-4 border-b border-gray-100 text-base font-medium transition-colors ${
                  active ? 'text-blue-600 bg-blue-50' : 'text-gray-800 hover:bg-gray-50'
                }`}
                data-testid={`mobile-link-${to.replace(/\W+/g, '') || 'home'}`}
              >
                {label}
              </Link>
            );
          })}
          <a
            href="https://www.instagram.com/minchiobrothers/"
            target="_blank"
            rel="noopener noreferrer"
            className="px-5 py-4 border-b border-gray-100 text-base font-medium text-gray-800 hover:bg-gray-50 flex items-center"
            onClick={() => setOpen(false)}
          >
            <i className="ri-instagram-line text-xl mr-3"></i> Instagram
          </a>
        </nav>
        <div className="px-5 py-5 mt-auto border-t border-gray-200 text-sm text-gray-600">
          <a href="tel:+61431323137" className="block text-blue-600 font-semibold mb-1">
            +61 431 323 137
          </a>
          <a href="mailto:media@matthewminchio.com" className="block text-gray-700">
            media@matthewminchio.com
          </a>
        </div>
      </aside>
    </>
  );
}
